<template>
    <figure :class="`image is-${size} is-avatar`" :title="title">
        <div v-if="showImage" class="avatar-image">
            <img :src="imgUrl" alt="" class="avatar-img" @error="handleImageError" />
        </div>
        <div v-else class="initials-avatar" :style="initialsAvatarStyle">
            <div v-if="skeleton && hasValidImageUrl && !imageLoadError && !imageLoaded" class="avatar-skeleton" />
            <span v-else>{{ calculatedInitials }}</span>
        </div>
    </figure>
</template>

<script>
import { initials } from '@/Helpers.js';

export default {
    name: 'UserAvatar',

    props: {
        size: {
            type: String,
            default: '48x48',
        },

        imgUrl: {
            type: null,
            default: null,
        },

        title: {
            type: null,
            default: null,
        },

        user: {
            type: Object,
            default: null,
        },

        backgroundColor: {
            type: String,
            default: null, // Will use a dynamically generated color if null
        },

        skeleton: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            imageLoadError: false,
            imageLoaded: false,
            preloadRequestId: 0,
            preloadImage: null,
        };
    },

    computed: {
        hasValidImageUrl() {
            return (
                this.imgUrl !== null &&
                this.imgUrl !== undefined &&
                typeof this.imgUrl === 'string' &&
                this.imgUrl.trim() !== ''
            );
        },

        showImage() {
            // Only mount <img> after a successful preload so the browser never
            // paints a broken-image icon while a bad URL is resolving.
            return this.hasValidImageUrl && this.imageLoaded && !this.imageLoadError;
        },

        calculatedInitials() {
            // First try to get initials from user object
            if (this.user) {
                // Handle both User models (which have an initials property) and plain objects
                if (this.user.initials) {
                    return this.user.initials;
                }

                // Fall back to calculating from first_name and last_name
                return initials(this.user.first_name, this.user.last_name);
            }

            // If no user object, try to extract from title/name
            if (this.title) {
                const name = typeof this.title === 'string' ? this.title.trim() : '';
                const nameParts = name.split(' ');

                if (nameParts.length >= 2) {
                    return initials(nameParts[0], nameParts[nameParts.length - 1]);
                }
                if (nameParts.length === 1) {
                    return initials(nameParts[0], '');
                }
            }

            return '';
        },

        initialsAvatarStyle() {
            const nameForColor = this.user?.name || this.title || this.calculatedInitials;

            return {
                backgroundColor: this.backgroundColor || this.generateColorFromString(nameForColor),
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: this.calculateFontSize(),
                fontWeight: 'bold',
            };
        },
    },

    watch: {
        imgUrl: {
            immediate: true,
            handler(url) {
                this.preloadAvatar(url);
            },
        },
    },

    beforeUnmount() {
        this.teardownPreload();
    },

    methods: {
        handleImageError() {
            // Preload succeeded but the mounted <img> can still fail (e.g. revoked
            // URL / cache race). Fall back to initials instead of a broken icon.
            this.imageLoadError = true;
            this.imageLoaded = false;
        },

        preloadAvatar(url) {
            this.teardownPreload();
            this.imageLoadError = false;
            this.imageLoaded = false;

            const requestId = this.preloadRequestId + 1;
            this.preloadRequestId = requestId;

            if (url === null || url === undefined || typeof url !== 'string' || url.trim() === '') {
                return;
            }

            const image = new Image();
            this.preloadImage = image;

            image.onload = () => {
                if (requestId !== this.preloadRequestId) {
                    return;
                }
                this.imageLoaded = true;
                this.imageLoadError = false;
            };

            image.onerror = () => {
                if (requestId !== this.preloadRequestId) {
                    return;
                }
                this.imageLoaded = false;
                this.imageLoadError = true;
            };

            image.src = url;
        },

        teardownPreload() {
            if (this.preloadImage) {
                this.preloadImage.onload = null;
                this.preloadImage.onerror = null;
                this.preloadImage = null;
            }
        },

        generateColorFromString(str) {
            if (!str) return '#6c757d'; // Default gray

            // Simple string hash to generate a color
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                // eslint-disable-next-line no-bitwise
                hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }

            // Generate a hue between 0 and 360
            const h = Math.abs(hash) % 360;

            // Use a fixed saturation and lightness for readability
            return `hsl(${h}, 65%, 55%)`;
        },

        calculateFontSize() {
            const [width] = this.size.split('x');
            const numWidth = parseInt(width, 10);
            return `${Math.max(numWidth / 2.5, 10)}px`;
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.is-avatar {
    border-radius: 50%;
    overflow: hidden;

    div {
        height: 100%;
        width: 100%;
    }

    .avatar-image {
        position: relative;
        height: 100%;
        width: 100%;
    }

    .avatar-img {
        height: 100%;
        width: 100%;
        object-fit: cover;
    }

    .avatar-skeleton {
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, $grey-lighter 25%, $grey-lightest 50%, $grey-lighter 75%);
        background-size: 200% 100%;
        animation: avatar-skeleton-shimmer 1.2s ease-in-out infinite;
    }

    @keyframes avatar-skeleton-shimmer {
        0% {
            background-position: 200% 0;
        }

        100% {
            background-position: -200% 0;
        }
    }

    .initials-avatar {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        text-align: center;
        color: white;
    }
}
</style>
